import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import AppColors from '../../utils/AppColors';
import { getUserAnalytics, getVendorAnalytics, getVoucherAnalytics, getRevenueAnalytics } from '../../services/api';

const Container = styled(Box)({
    padding: '2rem',
});

const PageTitle = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
});

const ChartContainer = styled(Paper)({
    padding: '1.5rem',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    height: '400px',
});

const CHART_COLORS = [AppColors.primaryDark, AppColors.primaryYellow, '#2196F3', '#4CAF50', '#FF5722'];

const Analytics = () => {
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('30');

    useEffect(() => {
        fetchAnalytics();
    }, [timeRange]);

    const fetchAnalytics = async () => {
        try {
            const [userAnalytics, vendorAnalytics, voucherAnalytics, revenueAnalytics] = await Promise.all([
                getUserAnalytics({ days: timeRange }),
                getVendorAnalytics({ days: timeRange }),
                getVoucherAnalytics({ days: timeRange }),
                getRevenueAnalytics({ days: timeRange })
            ]);

            setAnalytics({
                users: userAnalytics,
                vendors: vendorAnalytics,
                vouchers: voucherAnalytics,
                revenue: revenueAnalytics
            });
        } catch (err) {
            setError('Failed to fetch analytics data');
            console.error('Error fetching analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <PageTitle>Analytics Dashboard</PageTitle>
                <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        label="Time Range"
                    >
                        <MenuItem value="7">Last 7 days</MenuItem>
                        <MenuItem value="30">Last 30 days</MenuItem>
                        <MenuItem value="90">Last 3 months</MenuItem>
                        <MenuItem value="365">Last year</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <Typography variant="h6" gutterBottom>User Growth</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <LineChart data={analytics.users?.growth || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="users" stroke={AppColors.primaryDark} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <Typography variant="h6" gutterBottom>Revenue Trends</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart data={analytics.revenue?.trends || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="revenue" fill={AppColors.primaryYellow} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <Typography variant="h6" gutterBottom>Vendor Activity</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <PieChart>
                                <Pie
                                    data={analytics.vendors?.activity || []}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {(analytics.vendors?.activity || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ChartContainer>
                        <Typography variant="h6" gutterBottom>Voucher Redemptions</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart data={analytics.vouchers?.redemptions || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="redemptions" fill={AppColors.successGreen} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Analytics;
